import type { CardSlot } from '@kenocyber/shared';
import { PVP_CODE_LENGTH } from '@kenocyber/shared';
import { useState } from 'react';
import { pvpHudView } from './hud';
import type { PvpUiState } from './machine';
import { PvpHud } from './PvpHud';
import { pvpResultText } from './result-text';
import type { PvpPresentation } from './selectors';

/**
 * Everything the duel shows, and nothing it decides.
 *
 * ## What this component is not allowed to know
 *
 * There is no `fetch`, no socket, no ticket, no reducer and no timer here. It
 * receives a state and a set of flags and renders them; every action is a
 * callback the controller owns. That is why the interesting cases - a double
 * click, an answer arriving after the player left, a bootstrap failing twice -
 * are proven without a renderer.
 *
 * The one piece of local state it does keep is the CODE BEING TYPED, which is
 * exactly the kind of thing a text field owns and nothing else needs.
 *
 * ## It says what the mode is, plainly
 *
 * A duel pays nothing. That is not a footnote here: it is on the first screen,
 * because a player deciding whether to fight should not have to find out
 * afterwards.
 */

export interface PvpPanelProps {
  readonly state: PvpUiState;
  readonly presentation: PvpPresentation;
  readonly onCreate: () => void;
  readonly onJoin: (code: string) => void;
  /** Called on the first edit after a refusal, so the message goes with it. */
  readonly onCodeEdited: () => void;
  readonly onReady: () => void;
  readonly onLeave: () => void;
  readonly onRetryRecovery: () => void;
  /**
   * Gives up a duel the server says this account is already in.
   *
   * Offered only when the refusal is `already_in_match`, because that is the
   * one refusal a player cannot act on: the room that would take the leaving
   * intent is exactly the room they cannot reach.
   */
  readonly onCancelStuck: () => void;
  /** Opens the arena's target mode. The HUD's teleport badge becomes a button. */
  readonly onArmTeleport: () => void;
  /** Manda rebentar as marcas deste jogador (gate V28G.N). */
  readonly onDetonateBleed: () => void;
  /** A ORDEM de ligar uma carta, do HUD para a cena (gate V28H.J). */
  readonly onActivateCard: (slot: CardSlot) => void;
  /** E a soltura da carga (gate V28H.N). */
  readonly onReleaseCard?: ((slot: CardSlot) => void) | undefined;
  /** Se há uma estrutura encostada, e a ordem de entrar nela (gate V28H.X). */
  readonly canBoard?: boolean | undefined;
  readonly onBoard?: (() => void) | undefined;
}

export function PvpPanel({
  state,
  presentation,
  onArmTeleport: armTeleport,
  onDetonateBleed: detonateBleed,
  onActivateCard: activateCard,
  onReleaseCard: releaseCard,
  canBoard,
  onBoard,
  onCreate,
  onJoin,
  onCodeEdited,
  onReady,
  onLeave,
  onRetryRecovery,
  onCancelStuck,
}: PvpPanelProps) {
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);

  /*
   * The dedicated mode is a SURFACE, not an overlay.
   *
   * A duel replaces the game rather than floating over it, so the container
   * changes class instead of the App deciding what to hide - the flags already
   * say, and one of them says exactly this.
   */
  const className = presentation.dedicatedMode ? 'pvp-surface' : 'pvp-panel';

  return (
    <section className={className} data-testid="pvp-panel" aria-label="Duelo 1x1">
      {/*
       * The lobby's heading, and only the lobby's.
       *
       * In the dedicated mode the screen belongs to the arena: a title and a
       * paragraph across the top would be two lines of chrome over a fight,
       * and the HUD already carries `SEM RECOMPENSA` where the player is
       * looking. Kept in the lobby, where it is the first thing they read.
       */}
      {presentation.dedicatedMode ? null : (
        <header className="pvp-panel-head">
          <h2>PVP PRIVADO 1X1</h2>
          <p className="pvp-no-reward" data-testid="pvp-no-reward">
            SEM RECOMPENSAS — nenhum Gold, XP ou item muda de mãos.
          </p>
        </header>
      )}

      <div className="pvp-panel-body">{body()}</div>

      {presentation.canClose ? (
        <>
          {/*
           * The intent never reached the room, so nothing was abandoned.
           *
           * Said beside the button rather than over the arena: the player is
           * still in the fight, and a refusal about leaving must not replace the
           * match they are still playing.
           */}
          {presentation.abandonFailed ? (
            <p className="pvp-error" role="alert" data-testid="pvp-abandon-error">
              Não foi possível abandonar a partida. Tente novamente.
            </p>
          ) : null}
          {/*
           * One button, and its wording follows the situation.
           *
           * A result screen with both "SAIR" and "RETORNAR AO JOGO" would be two
           * buttons for one action; after a duel has ended, going back to the
           * hunt IS leaving. During a fight it is neither: pressing it forfeits
           * the match, and the label says exactly that before it is pressed.
           */}
          <button
            type="button"
            className="pvp-leave"
            data-testid="pvp-leave"
            onClick={onLeave}
            /*
             * Busy while the intent travels. The confirmation is the server's
             * `ended` frame, and a second press before it arrives would be a
             * second intent for a match that is already being decided.
             */
            disabled={presentation.abandonPending}
          >
            {leaveLabel()}
          </button>
        </>
      ) : null}
    </section>
  );

  /**
   * What the one exit button says right now.
   *
   * Order matters: the busy and failed wordings belong to a fight in progress and
   * must win over the generic ones, and `SAIR` is what is left for a lobby, an
   * invite nobody took and a socket that is coming back.
   */
  function leaveLabel(): string {
    if (presentation.abandonPending) {
      return 'ABANDONANDO…';
    }
    if (presentation.abandonFailed) {
      return 'TENTAR NOVAMENTE';
    }
    if (presentation.showsResult || presentation.showsError) {
      return 'RETORNAR AO JOGO';
    }
    // Unambiguous on purpose: during a fight this forfeits the match, and "SAIR"
    // reads like closing a panel.
    return state.kind === 'running' ? 'ABANDONAR PARTIDA' : 'SAIR';
  }

  function body() {
    switch (state.kind) {
      case 'choosing':
        return (
          <div className="pvp-choice">
            {/*
             * A refused code, right above the field that produced it.
             *
             * This used to be a screen of its own that REPLACED the form, so
             * the only way to try a second code was to close the panel and
             * reopen it. Nothing is held by a join that failed - no lease, no
             * socket, no arena - so there was never anything to unwind, only
             * a form to keep.
             */}
            {state.lastError ? (
              <p className="pvp-error" role="alert" data-testid="pvp-join-error">
                {errorText(state.lastError.reason)}
              </p>
            ) : null}
            {/*
             * The way out of the one refusal that had none (gate V27A).
             *
             * `already_in_match` used to be a sentence and nothing else: the
             * player could not resume the duel, because its room was gone, and
             * could not leave it, because leaving is an intent sent from inside
             * that room. Measured on a real machine, two accounts sat barred
             * until the database was edited by hand.
             *
             * It is not offered for any other refusal. A code that does not
             * exist is not a duel to cancel, and a full room is somebody else's.
             */}
            {state.lastError?.reason === 'already_in_match' ? (
              <button
                type="button"
                className="pvp-cancel-stuck"
                data-testid="pvp-cancel-stuck"
                onClick={onCancelStuck}
              >
                CANCELAR DESAFIO ANTERIOR
              </button>
            ) : null}
            <button
              type="button"
              className="pvp-create"
              data-testid="pvp-create"
              onClick={onCreate}
            >
              CRIAR DESAFIO
            </button>
            <form
              className="pvp-join"
              onSubmit={(event) => {
                event.preventDefault();
                submitCode();
              }}
            >
              <label htmlFor="pvp-code">Código do desafio</label>
              <input
                id="pvp-code"
                data-testid="pvp-code-input"
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  // The refusal was about the old code. It goes with it.
                  onCodeEdited();
                }}
                /*
                 * The length the contract states, as a hint and a bound. The
                 * SERVER is still the authority on whether a code exists - this
                 * only stops an obviously impossible one from becoming a
                 * request.
                 */
                maxLength={PVP_CODE_LENGTH}
                autoComplete="off"
                spellCheck={false}
                placeholder="ABC234"
              />
              <button
                type="submit"
                data-testid="pvp-join"
                // Blocked while empty, so a double press cannot send twice.
                disabled={code.trim().length === 0}
              >
                {/*
                 * The label says what pressing it will do. After a refusal
                 * "ACEITAR DESAFIO" reads as if the last attempt had not
                 * happened.
                 */}
                {state.lastError ? 'TENTAR NOVAMENTE' : 'ACEITAR DESAFIO'}
              </button>
            </form>
          </div>
        );

      case 'creating':
      case 'joining':
        return (
          <p className="pvp-busy" data-testid="pvp-busy">
            Falando com o servidor…
          </p>
        );

      case 'waiting_for_opponent':
        return (
          <div className="pvp-waiting">
            <p className="pvp-code-label">Código do desafio</p>
            <p className="pvp-code" data-testid="pvp-code">
              {state.code}
            </p>
            <button
              type="button"
              data-testid="pvp-copy"
              onClick={() => {
                /*
                 * Copying is the UI's business and may simply not be available:
                 * an insecure origin, a permission the player refused, an
                 * embedded webview. It must never take the app down over a
                 * convenience - the code is on screen either way.
                 */
                void navigator.clipboard
                  ?.writeText(state.code)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
            >
              COPIAR CÓDIGO
            </button>
            {copied ? (
              <span className="pvp-copied" data-testid="pvp-copied" role="status">
                Copiado.
              </span>
            ) : null}
            <p data-testid="pvp-waiting">AGUARDANDO ADVERSÁRIO…</p>
            {/*
             * The hunt is still running behind this. Said out loud so nobody
             * waiting on a code thinks they have left the game.
             */}
            <p className="pvp-hint">Sua caçada continua enquanto você espera.</p>
          </div>
        );

      case 'connecting':
        return (
          <p
            className="pvp-busy"
            data-testid={state.resumed ? 'pvp-reconnecting' : 'pvp-connecting'}
          >
            {state.resumed ? 'RECONECTANDO…' : 'Entrando na arena…'}
          </p>
        );

      case 'entering_pvp':
        return (
          <p className="pvp-busy" data-testid="pvp-connecting">
            Entrando na arena…
          </p>
        );

      case 'ready':
        return (
          <div className="pvp-ready">
            {/* The bars are already live: both ships exist and the room is
                complete. Only the clock is absent, because nothing is ticking. */}
            <PvpHud
              view={pvpHudView(state)}
              cardSlots={pvpHudView(state).cardSlots}
              onArmTeleport={armTeleport}
              onDetonateBleed={detonateBleed}
              onActivateCard={activateCard}
              onReleaseCard={releaseCard}
              canBoard={canBoard}
              onBoard={onBoard}
            />
            <p data-testid="pvp-ready">Adversário encontrado.</p>
            <button
              type="button"
              data-testid="pvp-ready-button"
              onClick={onReady}
              /*
               * One per generation, and the flag says which. Pressed twice, the
               * second press finds it disabled - and the confirmation comes
               * from the server, never from having pressed it.
               */
              disabled={!presentation.canReady}
            >
              {presentation.canReady ? 'PRONTO' : 'AGUARDANDO O SERVIDOR…'}
            </button>
          </div>
        );

      /*
       * From here the HUD speaks, not a sentence.
       *
       * `countdown` is unreachable today - the wire has no countdown to report -
       * and is handled beside the others so the screen exists when the contract
       * grows one. Until then the view model says PREPARANDO DUELO rather than
       * counting down from a number this client made up.
       */
      case 'countdown':
      case 'running':
      case 'reconnecting':
        return (
          <PvpHud
            view={pvpHudView(state)}
            cardSlots={pvpHudView(state).cardSlots}
            onArmTeleport={armTeleport}
            onDetonateBleed={detonateBleed}
            onActivateCard={activateCard}
            onReleaseCard={releaseCard}
            canBoard={canBoard}
            onBoard={onBoard}
          />
        );

      case 'completed': {
        // Straight from the exhaustive mapping. No default, no invented winner.
        const text = pvpResultText(state.result, state.match.seat);
        return (
          <div className={`pvp-result pvp-result-${text.outcome}`} data-testid="pvp-result">
            <h3 data-testid="pvp-result-title">{text.title}</h3>
            <p data-testid="pvp-result-detail">{text.detail}</p>
            {/*
             * Said again at the end, where a player might reasonably expect a
             * payout. The result screen replaces the HUD, so without this line
             * the one screen that closes the duel would be the one screen that
             * never mentions that nothing was won.
             */}
            <p className="pvp-no-reward" data-testid="pvp-no-reward">
              SEM RECOMPENSAS — nenhum Gold, XP ou item mudou de mãos.
            </p>
          </div>
        );
      }

      case 'leaving':
        return (
          <p className="pvp-busy" data-testid="pvp-leaving">
            Encerrando o duelo…
          </p>
        );

      case 'recovering_hunt':
        return (
          <div className="pvp-recovering" data-testid="pvp-recovering">
            <p>Retornando ao jogo…</p>
            {state.failure ? (
              <>
                {/*
                 * The hunt stays hidden behind this. Showing it before the
                 * server has re-authorised it would show a game out of date by
                 * a whole duel.
                 */}
                <p className="pvp-error" role="alert">
                  Não foi possível retomar a caçada.
                </p>
                <button type="button" data-testid="pvp-retry" onClick={onRetryRecovery}>
                  TENTAR NOVAMENTE
                </button>
              </>
            ) : null}
          </div>
        );

      case 'error':
        return (
          <div className="pvp-error-screen" data-testid="pvp-error">
            <p role="alert">{errorText(state.error.reason)}</p>
          </div>
        );

      default:
        // `closed` never reaches here: the panel is not rendered at all.
        return null;
    }
  }

  function submitCode() {
    const trimmed = code.trim();
    if (trimmed.length === 0) {
      return;
    }
    /*
     * The code STAYS in the field.
     *
     * It used to be cleared on submit, which was fine while a refusal replaced
     * the whole screen and was actively harmful the moment the form survived:
     * a player who mistyped one character got an empty box and had to type all
     * six again. Kept, it is one keystroke away from being right.
     */
    onJoin(trimmed);
  }
}

/**
 * A refusal, in words a player can act on.
 *
 * The server deliberately answers "unknown", "ended" and "not yours" with the
 * same reason so a code cannot be used to enumerate live rooms. This mapping
 * keeps that: there is one sentence for all three, and no branch that would
 * tell them apart.
 */
function errorText(reason: string): string {
  switch (reason) {
    case 'match_not_found':
      return 'Esse desafio não está disponível.';
    case 'match_not_joinable':
      return 'Esse desafio já tem dois jogadores.';
    case 'already_in_match':
      return 'Você já está em um duelo.';
    case 'pvp_disabled':
    case 'pvp_gold_pot_unavailable':
      return 'O duelo não está disponível nesta versão.';
    case 'superseded':
      return 'A partida foi assumida por outra aba.';
    case 'rate_limited':
      return 'Conexão encerrada por excesso de mensagens.';
    case 'malformed':
      return 'Erro de protocolo. Recarregue a página.';
    case 'unauthorised':
    case 'not_authorised':
      return 'Sua sessão não autoriza esse duelo.';
    case 'rules_version_mismatch':
      return 'Sua versão está desatualizada. Recarregue a página.';
    case 'reconnect_failed':
      return 'Não foi possível reconectar a tempo.';
    default:
      // A reason nobody mapped is still shown as a refusal, never as a result -
      // and never with the server's raw string, which is not written for a
      // player and could carry something a screenshot should not.
      return 'Não foi possível continuar o duelo.';
  }
}
